import React, { useState, useEffect } from 'react'
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Button,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { fetchSolutions } from '../../../lib/datocms'
import './Solutions.css'

const Solutions = () => {
  const [solutions, setSolutions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadSolutions()
  }, [])

  const loadSolutions = async () => {
    try {
      setLoading(true)
      const data = await fetchSolutions()
      setSolutions(data)
    } catch (err) {
      console.error('Error loading solutions:', err)
      setError('Failed to load solutions. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const renderSolutionCard = (solution) => {
    return (
      <Card key={solution.id} as={Link} to={`/solutions/${solution.slug}`} className="solution-card">
        <Card.Content>
          {solution.icon?.url && (
            <div className="solution-icon">
              <img src={solution.icon.url} alt={solution.icon.alt || solution.title} />
            </div>
          )}
          <Card.Header>{solution.title}</Card.Header>
          <Card.Description>{solution.shortDescription}</Card.Description>
        </Card.Content>
        <Card.Content extra>
          <Button primary fluid>
            Learn More <Icon name='arrow right' />
          </Button>
        </Card.Content>
      </Card>
    )
  }

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Solutions...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    )
  }

  return (
    <div className="solutions-page">
      {/* Hero Section */}
      <Segment className="solutions-hero" vertical>
        <Container text textAlign='center'>
          <Header as='h1' inverted>
            Wholesale Marketplaces/Exchanges
          </Header>
          <p className="hero-subtitle">
            Trading Systems is focused on empowering the wholesale B2B industries with comprehensive solutions
            for trading, inventory management, logistics coordination, and procurement of traditional wholesale items.
          </p>
          <Button primary size='large' as={Link} to='/contact-us'>
            Get Started
          </Button>
        </Container>
      </Segment>

      {/* Solutions Overview Section */}
      <Segment className="solutions-overview" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Simplify your material sourcing workflow
          </Header>
          
          <Grid columns={3} stackable>
            <Grid.Column>
              <div className="feature-box">
                <Icon name='search' size='huge' color='blue' />
                <Header as='h3'>Search</Header>
                <p>
                  AI-powered search to streamline your quotation process. 
                  Stop copying & pasting between different suppliers for pricing quotes.
                </p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="feature-box">
                <Icon name='clipboard check' size='huge' color='green' />
                <Header as='h3'>Source</Header>
                <p>
                  Seamlessly organize and process quotations from different suppliers. 
                  Schedule deliveries and monitor dispatch status in real-time.
                </p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="feature-box">
                <Icon name='chart line' size='huge' color='orange' />
                <Header as='h3'>Manage</Header>
                <p>
                  Track inventory levels across your supply chain. 
                  Monitor spending and generate comprehensive reports.
                </p>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Solutions Grid from Dato CMS */}
      {solutions.length > 0 && (
        <Segment className="solutions-grid" vertical>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
              Our Solutions
            </Header>
            <Card.Group itemsPerRow={3} stackable>
              {solutions.map(solution => renderSolutionCard(solution))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* How It Works Section */}
      <Segment className="how-it-works" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            How Trading Systems works for your wholesale marketplace/exchange
          </Header>
          
          <Grid columns={3} stackable>
            <Grid.Column textAlign='center'>
              <div className="step-circle">1</div>
              <Header as='h3'>Step 1</Header>
              <p>
                Set up your organization and invite team members. 
                Configure your product catalog and pricing rules based on your business needs.
              </p>
            </Grid.Column>
            <Grid.Column textAlign='center'>
              <div className="step-circle">2</div>
              <Header as='h3'>Step 2</Header>
              <p>
                Connect with suppliers and buyers in your network. 
                Start receiving quotes and placing orders through our unified platform.
              </p>
            </Grid.Column>
            <Grid.Column textAlign='center'>
              <div className="step-circle">3</div>
              <Header as='h3'>Step 3</Header>
              <p>
                Track orders, manage inventory, and coordinate logistics. 
                Generate reports and insights to optimize your operations.
              </p>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Dashboard Preview Section */}
      <Segment className="dashboard-preview" vertical>
        <Container>
          <Grid columns={2} stackable verticalAlign='middle'>
            <Grid.Column>
              <Header as='h2'>
                Insight and control all in one dashboard
              </Header>
              <p>
                Our intuitive dashboard gives you full visibility into your procurement metrics and 
                supply chain operations. Monitor key performance indicators and make data-driven decisions.
              </p>
              <div className="metrics-grid">
                <div className="metric-card">
                  <h3>Total Orders</h3>
                  <p className="metric-value">$15,450.00</p>
                </div>
                <div className="metric-card">
                  <h3>Number of Orders</h3>
                  <p className="metric-value">156</p>
                </div>
                <div className="metric-card">
                  <h3>Adjust Time Rates</h3>
                  <p className="metric-value">7.5%</p>
                </div>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="dashboard-image">
                {/* Placeholder for dashboard screenshot */}
                <Segment placeholder>
                  <Header icon>
                    <Icon name='chart bar' />
                    Dashboard Analytics
                  </Header>
                </Segment>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Supply Chain Risk Section */}
      <Segment className="supply-chain-risk" vertical>
        <Container>
          <Grid columns={2} stackable verticalAlign='middle'>
            <Grid.Column>
              <div className="risk-image">
                {/* Placeholder for supply chain image */}
                <Segment placeholder>
                  <Header icon>
                    <Icon name='truck' />
                    Supply Chain Management
                  </Header>
                </Segment>
              </div>
            </Grid.Column>
            <Grid.Column>
              <Header as='h2'>
                Minimize supply chain risk
              </Header>
              <p>
                Ensure your entire supply chain runs seamlessly with real-time inventory tracking and 
                supplier management. Trading Systems gives you unprecedented visibility into operations, 
                helps you identify potential bottlenecks, and ensures reliable supply chain continuity.
              </p>
              <div className="risk-metrics">
                <div className="risk-metric">
                  <h3>Risk Exposure</h3>
                  <p className="metric-value">45</p>
                  <p className="metric-label">Critical items monitored</p>
                </div>
                <div className="risk-metric">
                  <h3>Confirm Rate</h3>
                  <p className="metric-value">98%</p>
                  <p className="metric-label">Order accuracy</p>
                </div>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Key Features Section */}
      <Segment className="key-features" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Key Features for Success
          </Header>
          <p textAlign='center' style={{ marginBottom: '3em' }}>
            Understand more on the advanced tools that empower you to streamline operations, 
            make data-driven decisions, and scale your business efficiently.
          </p>
          
          <Grid columns={2} stackable>
            <Grid.Column>
              <div className="feature-detail">
                <Icon name='database' size='large' color='blue' />
                <div>
                  <Header as='h3'>Manage and schedule all of your shipments</Header>
                  <p>
                    Coordinate deliveries, track shipments in real-time, and optimize logistics 
                    operations with our comprehensive management tools.
                  </p>
                </div>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="feature-detail">
                <Icon name='users' size='large' color='green' />
                <div>
                  <Header as='h3'>Automated emails to buyers and suppliers for pricing updates</Header>
                  <p>
                    Keep all stakeholders informed with automated notifications about price changes, 
                    inventory updates, and order status changes.
                  </p>
                </div>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* CTA Section */}
      <Segment className="solutions-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Streamline your wholesale business
          </Header>
          <p>
            Join hundreds of businesses that have transformed their operations with Trading Systems. 
            Get started today and experience the power of our comprehensive B2B marketplace platform.
          </p>
          <Button size='large' inverted as={Link} to='/signup'>
            Start Free Trial
          </Button>
          <Button size='large' inverted basic as={Link} to='/contact-us'>
            Request Demo
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default Solutions